import CloseIcon from '@mui/icons-material/Close'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import RefreshIcon from '@mui/icons-material/Refresh'
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { PortalMatchDto } from '@src/data/api/fraud/fraud-api'
import { usePortalEnrichment, useRefreshPortalEnrichment } from '@src/data/api/fraud/useFraud'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface PortalEnrichmentDialogProps {
  open: boolean
  onClose: () => void
  financingProgramId: string
  creditApplicationId: string
  fraudIQApplicationId: string
}

export const PortalEnrichmentDialog: React.FC<PortalEnrichmentDialogProps> = ({
  open,
  onClose,
  financingProgramId,
  creditApplicationId,
  fraudIQApplicationId,
}) => {
  const { t } = useTranslation()
  const [refreshError, setRefreshError] = useState<string | null>(null)

  const { data, isFetching, error } = usePortalEnrichment(
    financingProgramId,
    creditApplicationId,
    fraudIQApplicationId,
    open,
  )
  const [refresh, isRefreshing] = useRefreshPortalEnrichment()

  const handleRefresh = async () => {
    try {
      await refresh({ financingProgramId, creditApplicationId, fraudIQApplicationId })
    } catch (err) {
      const message = err instanceof Error ? err.message : t('fraudFlags.portalEnrichment.refreshFailed', 'Failed to refresh portal enrichment')
      setRefreshError(message)
    }
  }

  const formatDate = (iso?: string) => (iso ? new Date(iso).toLocaleString() : '')
  const latestAttemptFailed = data?.lastAttemptedOn && new Date(data.lastAttemptedOn) > new Date(data.enrichedOn)

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="h6" component="span" fontWeight={600}>
              {t('fraudFlags.portalEnrichment.title', 'Portal Enrichment')}
            </Typography>
            {data?.enrichedOn && (
              <Typography variant="caption" color="text.secondary">
                {t('fraudFlags.portalEnrichment.enrichedOn', 'Scraped')}: {formatDate(data.enrichedOn)}
              </Typography>
            )}
            {latestAttemptFailed && (
              <Tooltip title={t('fraudFlags.portalEnrichment.latestAttemptFailed', 'The latest scrape attempt failed; showing previous successful data')}>
                <Alert severity="warning" sx={{ py: 0, px: 1 }}>
                  {t('fraudFlags.portalEnrichment.staleData', 'Stale')}
                </Alert>
              </Tooltip>
            )}
            {(isFetching || isRefreshing) && <CircularProgress size={16} />}
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip title={t('fraudFlags.portalEnrichment.refresh', 'Refresh')}>
              <span>
                <IconButton onClick={handleRefresh} disabled={isRefreshing} size="small">
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton onClick={onClose} size="small" aria-label="close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error">
              {t('fraudFlags.portalEnrichment.loadFailed', 'Failed to load portal enrichment')}: {error.message}
            </Alert>
          )}
          {!error && isFetching && !data && (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress />
            </Box>
          )}
          {!error && !data && !isFetching && (
            <Typography variant="body2" color="text.secondary">
              {t('fraudFlags.portalEnrichment.noData', 'No portal data available yet.')}
            </Typography>
          )}
          {data && (
            <Stack spacing={3}>
              <ValidationRulesTable rules={data.validationRules} />
              <MatchesTable
                title={t('fraudFlags.portalEnrichment.localMatches', 'Local Matches')}
                matches={data.localMatches}
              />
              <MatchesTable
                title={t('fraudFlags.portalEnrichment.nationalMatches', 'National Matches')}
                matches={data.nationalMatches}
              />
            </Stack>
          )}
        </DialogContent>
      </Dialog>
      <Snackbar
        open={!!refreshError}
        autoHideDuration={6000}
        onClose={() => setRefreshError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="error" onClose={() => setRefreshError(null)}>
          {refreshError}
        </Alert>
      </Snackbar>
    </>
  )
}

const ValidationRulesTable: React.FC<{ rules: { ruleName: string; matchDate: string }[] }> = ({ rules }) => {
  const { t } = useTranslation()
  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {t('fraudFlags.portalEnrichment.validationRules', 'Validation Rules')} ({rules.length})
      </Typography>
      {rules.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('fraudFlags.portalEnrichment.noRules', 'No validation rules fired.')}
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('fraudFlags.portalEnrichment.ruleName', 'Rule')}</TableCell>
                <TableCell>{t('fraudFlags.portalEnrichment.matchDate', 'Match Date')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map((r, i) => (
                <TableRow key={i}>
                  <TableCell>{r.ruleName}</TableCell>
                  <TableCell>{r.matchDate}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  )
}

const MatchesTable: React.FC<{ title: string; matches: PortalMatchDto[] }> = ({ title, matches }) => {
  const { t } = useTranslation()
  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {title} ({matches.length})
      </Typography>
      {matches.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('fraudFlags.portalEnrichment.noMatches', 'No matches found.')}
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>{t('fraudFlags.portalEnrichment.matchType', 'Type')}</TableCell>
                <TableCell>{t('fraudFlags.portalEnrichment.matchReferenceId', 'Reference')}</TableCell>
                <TableCell>{t('fraudFlags.portalEnrichment.matchName', 'Name')}</TableCell>
                <TableCell>{t('fraudFlags.portalEnrichment.matchAppDate', 'App Date')}</TableCell>
                <TableCell>{t('fraudFlags.portalEnrichment.matchStatus', 'Status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {matches.map((m, i) => (
                <MatchRow key={i} match={m} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  )
}

const MatchRow: React.FC<{ match: PortalMatchDto }> = ({ match }) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const hasRules = match.matchRules.length > 0

  return (
    <>
      <TableRow>
        <TableCell sx={{ width: 40 }}>
          <IconButton
            size="small"
            onClick={() => setOpen((v) => !v)}
            disabled={!hasRules}
            aria-label={open ? 'Collapse' : 'Expand'}
          >
            {open ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell>{match.type}</TableCell>
        <TableCell>{match.referenceId}</TableCell>
        <TableCell>{match.name}</TableCell>
        <TableCell>{match.appDate}</TableCell>
        <TableCell>{match.status}</TableCell>
      </TableRow>
      {open && hasRules && (
        <TableRow>
          <TableCell />
          <TableCell colSpan={5} sx={{ py: 1, backgroundColor: 'action.hover' }}>
            <Typography variant="caption" fontWeight={600} gutterBottom>
              {t('fraudFlags.portalEnrichment.matchRules', 'Match Rules')}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('fraudFlags.portalEnrichment.ruleDescription', 'Description')}</TableCell>
                  <TableCell>{t('fraudFlags.portalEnrichment.ruleDate', 'Date')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {match.matchRules.map((r, j) => (
                  <TableRow key={j}>
                    <TableCell>{r.description}</TableCell>
                    <TableCell>{r.date}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}
